import { Icon } from '@iconify/react'
import dayjs from 'dayjs'
import type { MouseEvent, PointerEvent } from 'react'
import { useParams } from 'react-router'

import { useAuth } from 'app/providers/auth/AuthProvider'

import { useAppDispatch, useAppSelector } from 'common/hooks/redux'

import { PATIENT_STATUSES } from 'features/Home/constants/infoConstants'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { useUpdateScheduleAppointmentMutation } from 'features/Schedule/api/schedule.api'
import type { ScheduleAppointmentDto } from 'features/Schedule/api/schedule.types'

import { openTelehealthSession } from '../state/jitsiTelehealth.slice'
import { isJitsiTelehealthAvailable } from '../utils/jitsiTelehealth.utils'

import styles from './jitsiTelehealthCallButton.module.scss'

type JitsiTelehealthCallButtonVariant = 'default' | 'icon'

type JitsiTelehealthCallButtonProps = {
  schedule: IPatientSchedule
  disabled?: boolean
  variant?: JitsiTelehealthCallButtonVariant
  onCallStarted?: (statusTypeCode: number) => void
}

const formatTimeForDto = (value: string | Date): string => {
  if (typeof value === 'string' && /^\d{2}:\d{2}(:\d{2})?$/.test(value)) {
    return value.length === 5 ? `${value}:00` : value
  }
  return dayjs(value).format('HH:mm:ss')
}

const buildAppointmentDtoFromSchedule = (
  schedule: IPatientSchedule,
  statusTypeCode: number,
): ScheduleAppointmentDto => ({
  room: schedule.room?.value ?? '',
  patient: {
    id: schedule.patient.id ?? 0,
    first_name: schedule.patient.first_name,
    last_name: schedule.patient.last_name,
    phone: schedule.patient.phone ?? '',
    language_type_code: schedule.patient.language_type_code ?? 0,
  },
  consultation_staff: schedule.consultation_staff,
  temperature_bypass: schedule.temperature_bypass,
  start_time: formatTimeForDto(schedule.start_time),
  end_time: formatTimeForDto(schedule.end_time),
  procedure_code: schedule.procedure?.code ?? null,
  procedure_description: schedule.procedure?.description ?? null,
  category_type_code: schedule.category?.code ?? 0,
  title: schedule.title,
  comments: schedule.comments ?? '',
  status_type_code: statusTypeCode,
  punctuality_type_code: schedule.punctuality?.appointment?.code ?? 0,
  punctuality_minutes: schedule.punctuality?.appointment?.minutes ?? 0,
})

export const JitsiTelehealthCallButton = ({
  schedule,
  disabled,
  variant = 'default',
  onCallStarted,
}: JitsiTelehealthCallButtonProps) => {
  const { isDoctor } = useAuth()
  const { siteId } = useParams()
  const activeAppointmentId = useAppSelector(
    (state) => state.jitsiTelehealthReducer.activeSession?.appointmentId ?? null,
  )
  const dispatch = useAppDispatch()
  const [updateScheduleAppointment] = useUpdateScheduleAppointmentMutation()

  const session = schedule.jitsi_telehealth

  if (!isDoctor || !isJitsiTelehealthAvailable(session)) {
    return null
  }

  const appointmentId = schedule.id
  const patientName = schedule.patient?.name ?? ''
  const isAlreadyActive = activeAppointmentId === appointmentId

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    dispatch(
      openTelehealthSession({
        appointmentId,
        doctor_embed_url: session.doctor_embed_url,
        patientName,
      }),
    )

    if (siteId && schedule.date) {
      updateScheduleAppointment({
        siteId,
        date: schedule.date,
        appointmentId,
        data: buildAppointmentDtoFromSchedule(schedule, PATIENT_STATUSES.IN_CALL),
      })
    }

    onCallStarted?.(PATIENT_STATUSES.IN_CALL)
  }

  // Claim the touch/pointer gesture early so the calendar chip (Syncfusion drag)
  // or the surrounding row click handler cannot steal the tap on tablets.
  const stopGesture = (event: PointerEvent<HTMLButtonElement>) => {
    event.stopPropagation()
  }

  const className = variant === 'icon' ? styles.buttonIcon : styles.button
  const iconSize = variant === 'icon' ? 18 : 20

  return (
    <button
      type='button'
      className={className}
      aria-label='Start telehealth call'
      title={isAlreadyActive ? 'Telehealth call already open' : 'Start telehealth call'}
      disabled={disabled || isAlreadyActive}
      onPointerDown={stopGesture}
      onClick={handleClick}>
      <Icon icon='fluent:video-person-call-24-regular' fontSize={iconSize} />
    </button>
  )
}
